/**
 * Dialog de configuração para node Atribuir Vendedor CRM
 * Permite configurar estratégia de seleção do deal e modo de atribuição
 * 
 * RELEASE: 1.0.0 - 2025-01-25 - Novo dialog de configuração CRM
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
// ScrollArea removido - usando scroll nativo para robustez (v1.1.0)
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { UserCheck, Search, User, Users, HelpCircle, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { FlowNode, CrmAssignSellerConfig } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';

interface ConfigureCrmAssignSellerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: CrmAssignSellerConfig) => void;
}

/**
 * Labels para exibição do filtro de status
 */
const STATUS_OPTIONS = [
  { value: 'open', label: 'Em Aberto', description: 'Apenas negócios em andamento' },
  { value: 'won', label: 'Concluído (Ganho)', description: 'Apenas negócios ganhos' },
  { value: 'lost', label: 'Perdido', description: 'Apenas negócios perdidos' },
  { value: 'all', label: 'Todos os Status', description: 'Qualquer status de negócio' },
];

/**
 * Labels para método de distribuição
 */
const METHOD_LABELS: Record<string, string> = {
  'round_robin': 'Rodízio',
  'least_busy': 'Menos Ocupado',
  'weighted': 'Ponderado',
};

/**
 * Dialog para configurar atribuição de vendedor CRM
 * Seleção de deal + Modo de atribuição (manual ou fila)
 */
export function ConfigureCrmAssignSellerDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureCrmAssignSellerDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estado do formulário
  const existingConfig = node.data.config as CrmAssignSellerConfig | undefined;
  
  // Estado para dialog de ajuda
  const [showHelp, setShowHelp] = useState(false);
  
  // ========================================
  // SELEÇÃO DO DEAL
  // ========================================
  
  const [dealSelection, setDealSelection] = useState<'most_recent' | 'from_variable' | 'from_trigger'>(
    existingConfig?.deal_selection || 'from_trigger'
  );
  
  // Variável do deal (leitura)
  const [dealVariableId, setDealVariableId] = useState(existingConfig?.deal_variable_id || '');
  const [dealVariableName, setDealVariableName] = useState(existingConfig?.deal_variable_name || '');
  
  const [dealStatusFilter, setDealStatusFilter] = useState<'open' | 'won' | 'lost' | 'all'>(
    existingConfig?.deal_status_filter || 'open'
  );
  
  // ========================================
  // MODO DE ATRIBUIÇÃO
  // ========================================
  
  const [sellerAssignmentMode, setSellerAssignmentMode] = useState<'manual' | 'distribution_queue'>(
    existingConfig?.seller_assignment_mode || 'manual'
  );
  
  // Vendedor manual
  const [sellerId, setSellerId] = useState(existingConfig?.seller_id || '');
  const [sellerName, setSellerName] = useState(existingConfig?.seller_name || '');
  
  // Fila de distribuição
  const [distributionQueueId, setDistributionQueueId] = useState(existingConfig?.distribution_queue_id || '');
  const [distributionQueueName, setDistributionQueueName] = useState(existingConfig?.distribution_queue_name || '');
  const [distributionQueueMethod, setDistributionQueueMethod] = useState(existingConfig?.distribution_queue_method || '');
  
  // Redistribuição forçada (apenas para modo fila)
  const [forceRedistribute, setForceRedistribute] = useState(
    existingConfig?.force_redistribute ?? false
  );

  // ========================================
  // QUERIES
  // ========================================

  // Query: Filas de distribuição com contexto CRM
  const { data: distributionQueues = [], isLoading: loadingQueues } = useQuery({
    queryKey: ['crm-distribution-queues', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, is_active')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // ========================================
  // HANDLERS
  // ========================================

  // Handler: Seleção de vendedor
  const handleSellerSelect = (id: string | null) => {
    setSellerId(id || '');
    // Buscar nome do vendedor se necessário - usar o valor do select
    if (id) {
      // Nome será obtido via hook no momento da renderização
      // Por ora, mantemos vazio e atualizamos via useCrmSellers
    }
  };

  // Handler: Seleção de fila
  const handleQueueSelect = (id: string) => {
    const queue = distributionQueues.find(q => q.id === id);
    setDistributionQueueId(id);
    setDistributionQueueName(queue?.name || '');
    setDistributionQueueMethod(queue?.distribution_method || '');
  };

  // Validação do formulário
  const isFormValid = () => {
    // Validar seleção do deal
    if (dealSelection === 'from_variable' && !dealVariableName.trim()) return false;
    
    // Validar modo de atribuição
    if (sellerAssignmentMode === 'manual' && !sellerId) return false;
    if (sellerAssignmentMode === 'distribution_queue' && !distributionQueueId) return false;
    
    return true;
  };

  // Handler: Salvar configuração
  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const config: CrmAssignSellerConfig = {
      // Seleção do deal
      deal_selection: dealSelection,
      deal_variable_name: dealSelection === 'from_variable' ? dealVariableName.trim() : undefined,
      deal_variable_id: dealSelection === 'from_variable' ? dealVariableId : undefined,
      deal_status_filter: dealSelection === 'most_recent' ? dealStatusFilter : 'open',
      
      // Modo de atribuição
      seller_assignment_mode: sellerAssignmentMode,
      
      // Vendedor manual
      seller_id: sellerAssignmentMode === 'manual' ? sellerId : undefined,
      seller_name: sellerAssignmentMode === 'manual' ? sellerName : undefined,
      
      // Fila de distribuição
      distribution_queue_id: sellerAssignmentMode === 'distribution_queue' ? distributionQueueId : undefined,
      distribution_queue_name: sellerAssignmentMode === 'distribution_queue' ? distributionQueueName : undefined,
      distribution_queue_method: sellerAssignmentMode === 'distribution_queue' ? distributionQueueMethod : undefined,
      
      // Redistribuição forçada (apenas para modo fila)
      force_redistribute: sellerAssignmentMode === 'distribution_queue' ? forceRedistribute : undefined,
    };

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* v1.1.0: Padrão robusto de scroll - altura fixa + scroll nativo */}
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-violet-500" />
                Configurar Atribuir Vendedor
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Atribua um vendedor a um negócio existente de forma manual ou automática via fila
            </DialogDescription>
          </DialogHeader>

          {/* Body com scroll nativo - compatível com wheel, trackpad e drag */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Campo ID do Node */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* ========================================
                  SEÇÃO: Seleção do Negócio
              ======================================== */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-500">
                  <Search className="h-4 w-4" />
                  Seleção do Negócio
                </div>

                {/* Modo de seleção */}
                <div className="space-y-3">
                  <Label>Como encontrar o negócio? *</Label>
                  <RadioGroup
                    value={dealSelection}
                    onValueChange={(v) => setDealSelection(v as 'most_recent' | 'from_variable' | 'from_trigger')}
                    className="space-y-2"
                  >
                    {/* Opção: Do negócio no gatilho CRM (padrão) */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="from_trigger" id="from_trigger_seller" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="from_trigger_seller" className="font-medium cursor-pointer flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Do negócio no gatilho CRM
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Usa o negócio que ativou o gatilho CRM ou de Evento de Contrato (padrão)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="most_recent" id="most_recent" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="most_recent" className="font-medium cursor-pointer">
                          Deal mais recente do contato
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Busca automaticamente o negócio mais recente associado ao contato
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="from_variable" id="from_variable" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="from_variable" className="font-medium cursor-pointer">
                          ID do deal em variável
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Usa o ID do negócio armazenado em uma variável do fluxo
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Filtro de status (apenas para most_recent) */}
                {dealSelection === 'most_recent' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label htmlFor="status-filter">Filtro de Status</Label>
                    <Select value={dealStatusFilter} onValueChange={(v) => setDealStatusFilter(v as typeof dealStatusFilter)}>
                      <SelectTrigger id="status-filter">
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex flex-col">
                              <span>{option.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Define qual status do negócio será considerado na busca
                    </p>
                  </div>
                )}

                {/* Variável do deal (apenas para from_variable) */}
                {dealSelection === 'from_variable' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label>Variável com ID do Deal *</Label>
                    <VariablePickerCombobox
                      tenantId={tenantId}
                      value={dealVariableId || dealVariableName}
                      onChange={(variable) => {
                        if (variable) {
                          setDealVariableId(variable.id);
                          // CORREÇÃO: Usar display_name para exportação correta de variáveis
                          setDealVariableName(variable.display_name || variable.name);
                        } else {
                          setDealVariableId('');
                          setDealVariableName('');
                        }
                      }}
                      allowCreate={false}
                      allowClear={false}
                      placeholder="Selecione a variável com o ID do deal"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use "ID do Negócio" (gatilho CRM) ou variável do node "Criar Negócio"
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              {/* ========================================
                  SEÇÃO: Atribuição do Vendedor
              ======================================== */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-500">
                  <User className="h-4 w-4" />
                  Atribuição do Vendedor
                </div>

                {/* Modo de atribuição */}
                <div className="space-y-3">
                  <Label>Como atribuir o vendedor? *</Label>
                  <RadioGroup
                    value={sellerAssignmentMode}
                    onValueChange={(v) => setSellerAssignmentMode(v as 'manual' | 'distribution_queue')}
                    className="space-y-2"
                  >
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="manual" id="manual" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="manual" className="font-medium cursor-pointer flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Vendedor Específico
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Selecione manualmente um vendedor cadastrado
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value="distribution_queue" id="distribution_queue" className="mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <Label htmlFor="distribution_queue" className="font-medium cursor-pointer flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Fila de Distribuição
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Distribuição automática via filas globais do CRM
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Seleção de vendedor (modo manual) */}
                {sellerAssignmentMode === 'manual' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label>Vendedor *</Label>
                    <SellerSelector
                      value={sellerId}
                      onSelect={(id) => {
                        setSellerId(id || '');
                        // Buscar nome do vendedor via cache do hook
                      }}
                      placeholder="Selecione o vendedor"
                    />
                  </div>
                )}

                {/* Seleção de fila (modo distribuição) */}
                {sellerAssignmentMode === 'distribution_queue' && (
                  <div className="space-y-2 pl-4 border-l-2 border-violet-200 dark:border-violet-800">
                    <Label>Fila de Distribuição *</Label>
                    <Select value={distributionQueueId} onValueChange={handleQueueSelect} disabled={loadingQueues}>
                      <SelectTrigger>
                        <SelectValue placeholder={loadingQueues ? 'Carregando...' : 'Selecione a fila'} />
                      </SelectTrigger>
                      <SelectContent>
                        {distributionQueues.map((queue) => (
                          <SelectItem key={queue.id} value={queue.id}>
                            <div className="flex items-center gap-2">
                              <span>{queue.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({METHOD_LABELS[queue.distribution_method] || queue.distribution_method})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      O vendedor será atribuído automaticamente seguindo as regras da fila selecionada
                    </p>
                    
                    {/* Opção de redistribuição forçada */}
                    <div className="mt-3 flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                      <div className="space-y-0.5">
                        <Label htmlFor="force-redistribute" className="font-medium cursor-pointer flex items-center gap-1.5">
                          <Zap className="h-3.5 w-3.5 text-amber-500" />
                          Redistribuir negócios já atribuídos
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Se ativado, substitui o vendedor atual por um novo da fila
                        </p>
                      </div>
                      <Switch
                        id="force-redistribute"
                        checked={forceRedistribute}
                        onCheckedChange={setForceRedistribute}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!isFormValid()}
              className="bg-violet-500 hover:bg-violet-600"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda */}
      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="crm_assign_seller" 
      />
    </>
  );
}
